import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeft, Scroll } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function Regolamento() {
  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["rules"],
    queryFn: async () => (await api.get("/rules")).data,
  });

  return (
    <div data-testid="regolamento-page" className="min-h-screen bg-[#0A0A0B]">
      <Header />
      <main className="pt-32 pb-24 px-5 sm:px-8 hero-spotlight">
        <div className="max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-[#C5A059] font-mono mb-4"
          >
            Neapolis Free Chapter · Est. 2019
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#F4EBE1]"
          >
            Il Regolamento
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-5 text-sm sm:text-base text-[#C0B9B0] leading-relaxed max-w-2xl"
          >
            Le regole che ogni socio del Neapolis Free Chapter condivide e rispetta,
            sulla strada e nella vita del club.
          </motion.p>

          {isLoading && (
            <div className="mt-14 space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="leather-card h-24 animate-pulse" />
              ))}
            </div>
          )}

          <div className="mt-14 space-y-5" data-testid="rules-list">
            {rules.map((rule, i) => (
              <motion.article
                key={rule.id}
                data-testid={`rule-${rule.id}`}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="leather-card p-6 sm:p-8 flex gap-6"
              >
                <span className="font-display text-4xl sm:text-5xl font-black text-gold-gradient leading-none shrink-0 w-14">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2 className="font-display text-lg sm:text-xl font-semibold text-[#F4EBE1]">
                    {rule.title}
                  </h2>
                  <p className="mt-2 text-sm text-[#C0B9B0] leading-relaxed">{rule.text}</p>
                </div>
              </motion.article>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 leather-card p-6 flex items-center gap-4"
            data-testid="rules-footer-note"
          >
            <Scroll className="w-6 h-6 text-[#C5A059] shrink-0" />
            <p className="text-sm text-[#C0B9B0] leading-relaxed">
              Il regolamento viene letto e accettato da ogni nuovo socio all'ingresso nel
              Neapolis Free Chapter. Il direttivo può aggiornarlo in assemblea.
            </p>
          </motion.div>

          <Link
            to="/"
            data-testid="regolamento-back-home"
            className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#78746D] hover:text-[#C5A059] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Torna alla Home
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
